import React, { useState, useEffect, useRef } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Box, Container, Typography, CircularProgress } from "@mui/material";
import ExportHeader from "../components/impact/ExportHeader";
import StatsOverview from "../components/impact/StatsOverview";
import AgeDistribution from "../components/impact/AgeDistribution";
import AgenciesEngaged from "../components/impact/AgenciesEngaged";
import AgencyCoverage from "../components/impact/AgencyCoverage";
import InterviewsConducted from "../components/impact/InterviewsConducted";
import VideosUploaded from "../components/impact/VideosUploaded";
import QualifiedSavings from "../components/impact/QualifiedSavings";
import { states, mapApiToDashboardData } from "../data/impactData";
import { DashboardData } from "../types";
import { ImpactApi } from "../utils/api";
import { useParams, useSearchParams } from "react-router-dom";

const theme = createTheme({
  palette: {
    primary: {
      main: "#592b7b",
      light: "#8B5CF6",
      dark: "#553C9A",
    },
    secondary: {
      main: "#3B82F6",
      light: "#60A5FA",
      dark: "#2563EB",
    },
    background: {
      default: "#F8FAFC",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1E293B",
      secondary: "#64748B",
    },
  },
  typography: {
    fontFamily: '"Lato"',
    h4: {
      fontWeight: 700,
      fontSize: "1.75rem",
      lineHeight: 1.2,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.3,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.4,
    },
    body1: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.75rem",
      lineHeight: 1.4,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: "none",
          border: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          // Only apply paddingBottom: 0 for export components
          "&.export-card-content": {
            paddingBottom: 0,
          },
        },
      },
    },
  },
});

function ExportViewPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  // ?state=XX (case-insensitive). Only used when no centerId is in the path —
  // a centerId is more specific and wins. Invalid codes silently fall back to
  // the nationwide view.
  const stateQueryParam = (searchParams.get("state") || "").toLowerCase();
  const initialStateValue =
    !id && stateQueryParam && states.some((s) => s.value === `us-${stateQueryParam}`)
      ? `us-${stateQueryParam}`
      : "";

  const hasLoaded = useRef(false);
  const [mapState, setMapState] = React.useState(initialStateValue || "All States");
  const [stateOrOrganization, setStateOrOrganization] = useState("");
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [centerState, setCenterState] = useState<string>(initialStateValue);
  const [centerName, setCenterName] = useState<string>("");
  const [useVerbiage, setUseVerbiage] = useState<
    "state" | "organization" | "nationwide"
  >("nationwide");

  const DESIGN_WIDTH = 1240;
  const DESIGN_HEIGHT = 816;

  async function loadDashboardData() {
    setLoading(true);
    setError(null);
    const stateForApi = initialStateValue
      ? initialStateValue.slice(-2).toUpperCase()
      : undefined;
    Promise.all([
      ImpactApi.getImpactReportData(stateForApi),
      ImpactApi.getLocationData(stateForApi),
    ])
      .then(([impact, locations]) => {
        setDashboardData(
          mapApiToDashboardData(
            (impact as any).impactReport as Record<string, unknown>[],
            locations,
            id,
            (impact as any).agenciesCount
          )
        );
      })
      .catch((error) => {
        console.log(error);
        setError("Failed to load impact data");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (dashboardData?.centerName && dashboardData.centerName !== "") {
      setCenterName(dashboardData.centerName);
    }
    if (dashboardData?.state) {
      const formattedState = `us-${dashboardData.state.toLowerCase()}`;
      setCenterState(formattedState);
    }
  }, [dashboardData]);

  useEffect(() => {
    if (!hasLoaded.current) {
      loadDashboardData();
      hasLoaded.current = true;
    }
  }, []);

  // Auto-detect and set appropriate verbiage and state based on data
  useEffect(() => {
    if (dashboardData) {
      if (id && centerName) {
        // If we have an ID and center name, this is organization data
        setUseVerbiage("organization");
        setStateOrOrganization(centerName);
        setMapState(centerState || "All States");
      } else if (centerState && centerState !== "All States") {
        // If we have a specific state, use state verbiage
        const stateName =
          states.find((state) => state.value === centerState)?.name || "";
        setStateOrOrganization(stateName);
        setUseVerbiage("state");
        setMapState(centerState);
      } else {
        // Default to nationwide
        setUseVerbiage("nationwide");
        setStateOrOrganization("");
        setMapState("All States");
      }
    }
  }, [dashboardData, id, centerName, centerState]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress size={48} thickness={4} color="primary" />
          <Typography>Loading impact data...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography color="error" variant="h6">
            {error}
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (!dashboardData) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography color="text.secondary" variant="h6">
            No data available
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          "--scale": `min(100vw / ${DESIGN_WIDTH}px, 100vh / ${DESIGN_HEIGHT}px)`,
          width: "100vw",
          height: "100vh",
          bgcolor: "background.default",
          background: "white",
          padding: 0,
          margin: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `calc(${DESIGN_WIDTH}px * var(--scale))`,
            height: `calc(${DESIGN_HEIGHT}px * var(--scale))`,
            position: "relative",
          }}
        >
          <Box
            sx={{
              height: `${DESIGN_HEIGHT}px`,
              width: `${DESIGN_WIDTH}px`,
              bgcolor: "background.default",
              background: "white",
              boxShadow:
                "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
              position: "absolute",
              top: 0,
              left: 0,
              transform: "scale(var(--scale))",
              transformOrigin: "top left",
            }}
          >
            <Container sx={{ width: "100%", m: 0, p: 0 }}>
              <ExportHeader
                stateOrOrganization={stateOrOrganization}
                organization={centerName}
                useVerbiage={useVerbiage}
                isViewPage={true}
              />

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "540px 600px",
                    maxHeight: "150px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      height: "100%",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h3"
                      component="h1"
                      sx={{ fontWeight: 1000, color: "primary.main" }}
                    >
                      IMPACT REPORT
                    </Typography>
                    <Typography
                      variant="body1"
                      component="p"
                      sx={{ fontWeight: 400, color: "text.secondary" }}
                    >
                      Forwarding the Mission of Child Protection
                    </Typography>
                  </Box>
                  <StatsOverview data={dashboardData.stats} isExport={true} />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "4fr 4fr 5fr",
                    height: "290px",
                  }}
                >
                  <InterviewsConducted
                    data={dashboardData.interviewsConducted}
                    useVerbiage={useVerbiage}
                    stateOrOrganization={stateOrOrganization}
                    isExport={true}
                  />
                  <AgeDistribution
                    data={dashboardData.ageDistribution}
                    useVerbiage={useVerbiage}
                    stateOrOrganization={stateOrOrganization}
                    isExport={true}
                  />

                  <AgencyCoverage
                    data={dashboardData.agencyCoverage}
                    mapState={mapState}
                    isExport={true}
                  />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "3fr 3fr 5fr",
                    gap: 3,
                    height: "290px",
                    pt: 3,
                  }}
                >
                  <VideosUploaded
                    data={dashboardData.videosUploaded}
                    useVerbiage={useVerbiage}
                    stateOrOrganization={stateOrOrganization}
                    isExport={true}
                  />
                  <AgenciesEngaged
                    data={dashboardData.agenciesEngaged}
                    isExport={true}
                  />

                  <QualifiedSavings
                    data={dashboardData.qualifiedSavings}
                    useVerbiage={useVerbiage}
                    stateOrOrganization={stateOrOrganization}
                    isExport={true}
                  />
                </Box>
              </Box>
            </Container>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default ExportViewPage;
